import { ReactNode, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Loader from './Loader';
import { navigation } from '../menu';

interface PageProtectedRouteProps {
  children: ReactNode;
  pageKey: string;
}

function isNavigationItemAccessible(
  item: typeof navigation[0],
  hasPageAccess: (pageKey: string) => boolean
): boolean {
  if (item.children && item.children.length > 0) {
    return item.children.some((child) => hasPageAccess(child.pageKey));
  }
  return hasPageAccess(item.pageKey);
}

function getRedirectUrlForNavigationItem(
  item: typeof navigation[0],
  hasPageAccess: (pageKey: string) => boolean
): string | null {
  if (item.children && item.children.length > 0) {
    const firstAccessibleChild = item.children.find((child) =>
      hasPageAccess(child.pageKey)
    );
    if (!firstAccessibleChild) return null;
    if (firstAccessibleChild.href) return firstAccessibleChild.href;
    // Sub-group: find the first accessible leaf
    const firstSub = firstAccessibleChild.children?.find((sub) =>
      hasPageAccess(sub.pageKey)
    );
    return firstSub?.href || null;
  }
  return item.href || null;
}

function findFirstAccessiblePageUrl(
  hasPageAccess: (pageKey: string) => boolean
): string {
  const firstAccessiblePage = navigation.find((item) => 
    isNavigationItemAccessible(item, hasPageAccess)
  );

  if (firstAccessiblePage) {
    return getRedirectUrlForNavigationItem(firstAccessiblePage, hasPageAccess) || '/dashboard';
  }

  return '/dashboard';
}

export function PageProtectedRoute({ children, pageKey }: PageProtectedRouteProps) {
  const { user, hasPageAccess, isLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Wait for auth to load
    if (isLoading) {
      return;
    }

    // Check if user has access to this page
    if (!user) {
      navigate('/login', { replace: true });
      return;
    }

    if (!hasPageAccess(pageKey)) {
      const redirectUrl = findFirstAccessiblePageUrl(hasPageAccess);
      navigate(redirectUrl, { replace: true });
    }
  }, [user, pageKey, hasPageAccess, isLoading, navigate]);

  // Show loading state while checking
  if (isLoading) {
    return <Loader />;
  }

  // If user doesn't have access, show loader while redirect runs (avoids blank white page)
  if (!user || !hasPageAccess(pageKey)) {
    return <Loader />;
  }

  return <>{children}</>;
}
